import React, { useContext, useEffect, useRef, useState } from "react";
import LanguageContext from "../helper/LanguageContext";
import { callAPI } from "../helper/apiUtils";
import MaterialIcon from "./MaterialIcon";
import { linkifyElement } from "../helper/emailBodyFormat";

/**
 * Insert a suggestion into the email WYSIWYG editor, keeping the signature.
 */
export function insertEmailReplySuggestion(editor, text) {
  if (!editor || !text) return "";
  const escaped = String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>");
  const sig = editor.querySelector(".email-signature-block");
  const sigHtml = sig ? sig.outerHTML : "";
  editor.innerHTML = `<div>${escaped}</div>${sigHtml ? `<div><br></div>${sigHtml}` : ""}`;
  linkifyElement(editor);
  return editor.innerHTML;
}

export function editorHasUserDraft(editor) {
  if (!editor) return false;
  const clone = editor.cloneNode(true);
  clone.querySelectorAll(".email-signature-block").forEach((node) => node.remove());
  const text = String(clone.innerText || "")
    .replace(/\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return false;
  if (/^---\s/.test(text) && text.length < 500) return false;
  return text.length > 8;
}

function normalizeList(data) {
  if (Array.isArray(data?.suggestions) && data.suggestions.length) {
    return data.suggestions
      .map((item, index) => {
        if (typeof item === "string") {
          return { id: `s-${index}`, label: "", body: item.trim() };
        }
        const body = String(item?.body || item?.text || "").trim();
        if (!body) return null;
        return {
          id: item.id || item.label || `s-${index}`,
          label: String(item.label || ""),
          body,
          requiresReview: item.requiresReview === true,
        };
      })
      .filter(Boolean);
  }
  if (Array.isArray(data?.suggestion)) {
    return data.suggestion
      .filter((s) => typeof s === "string" && s.trim())
      .map((body, index) => ({ id: `s-${index}`, label: "", body: body.trim() }));
  }
  return [];
}

/**
 * Chip UI for email reply suggestions. Same look as chat suggestions, but
 * chips show the actual generated text (not Positive / Neutral / Negative).
 */
export default function EmailReplySuggestions({ email, enabled, disabled, onSelect, hasDraft }) {
  const { strings } = useContext(LanguageContext);
  const [items, setItems] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [pendingOverwrite, setPendingOverwrite] = useState(null);
  const fetchedForRef = useRef(null);
  const overwritePanelRef = useRef(null);

  const emailId = email?.id || email?._id || null;

  const labelText = (label) => {
    if (label === "concise") return strings.email_suggestion_concise || "Concise";
    if (label === "balanced") return strings.email_suggestion_balanced || "Balanced";
    if (label === "warm") return strings.email_suggestion_warm || "Warm";
    return "";
  };

  const fetchSuggestions = (options = {}) => {
    if (!emailId) return;
    setLoading(true);
    setError(false);
    return callAPI(`/email/messages/${emailId}/suggestions`, {}, "POST", options)
      .then((response) => {
        const data = response?.data || {};
        setItems(normalizeList(data).slice(0, 3));
        setWarnings(Array.isArray(data.warnings) ? data.warnings : []);
        if (data.recipient?.needsUserConfirmation && !((data.warnings || []).includes("recipient_unconfirmed"))) {
          setWarnings((prev) => prev.concat("recipient_unconfirmed"));
        }
      })
      .catch(() => {
        setItems([]);
        setWarnings([]);
        setError(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setSelectedIndex(-1);
    setPendingOverwrite(null);
    setItems([]);
    setWarnings([]);
    setError(false);
    fetchedForRef.current = null;
  }, [emailId]);

  useEffect(() => {
    if (!enabled || !emailId) return;
    if (fetchedForRef.current === emailId) return;
    fetchedForRef.current = emailId;
    let cancelled = false;
    setLoading(true);
    setItems([]);
    setSelectedIndex(-1);
    setPendingOverwrite(null);
    callAPI(`/email/messages/${emailId}/suggestions`, {}, "POST")
      .then((response) => {
        if (cancelled) return;
        const data = response?.data || {};
        setItems(normalizeList(data).slice(0, 3));
        const nextWarnings = Array.isArray(data.warnings) ? data.warnings.slice() : [];
        if (data.recipient?.needsUserConfirmation && !nextWarnings.includes("recipient_unconfirmed")) {
          nextWarnings.push("recipient_unconfirmed");
        }
        setWarnings(nextWarnings);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, emailId]);

  const applySuggestion = (item, index) => {
    if (!item?.body) return;
    setSelectedIndex(index);
    setPendingOverwrite(null);
    onSelect(item.body);
  };

  const handleClick = (item, index) => {
    if (disabled || !item?.body) return;
    if (typeof hasDraft === "function" && hasDraft()) {
      if (pendingOverwrite?.index === index) {
        applySuggestion(item, index);
        return;
      }
      setPendingOverwrite({ item, index });
      return;
    }
    applySuggestion(item, index);
  };

  const regenerate = (tone, length) => {
    if (disabled || loading || !emailId) return;
    setSelectedIndex(-1);
    setPendingOverwrite(null);
    fetchSuggestions({ regenerate: true, tone, length });
  };

  useEffect(() => {
    if (!pendingOverwrite) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setPendingOverwrite(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [pendingOverwrite]);

  useEffect(() => {
    if (!pendingOverwrite) return;
    overwritePanelRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [pendingOverwrite]);

  if (!enabled || !emailId) return null;
  if (!loading && !error && items.length === 0) return null;

  return (
    <div
      className="suggestions-container suggestions-fade-in email-reply-suggestions"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="suggestions-wrapper">
        <div className="suggestions-header">
          <MaterialIcon icon="stars_2" size={14} color="#e066ed" />
          <span>{strings.suggested_replies || "Suggested Replies"}</span>
        </div>
        {warnings.includes("recipient_unconfirmed") && (
          <p className="email-reply-suggestion-note">
            {strings.email_suggestion_check_recipient || "Please check the greeting — the recipient could not be confirmed."}
          </p>
        )}
        {(warnings.includes("requires_review") || items.some((item) => item.requiresReview)) && (
          <p className="email-reply-suggestion-note">
            {strings.email_suggestion_review || "Review this draft before sending. It may include assumed details."}
          </p>
        )}
        {loading && items.length === 0 ? (
          <div className="suggestions-loading">
            <div className="suggestions-shimmer">
              <div className="shimmer-chip" />
              <div className="shimmer-chip" />
              <div className="shimmer-chip" />
            </div>
          </div>
        ) : error && items.length === 0 ? (
          <p className="email-reply-suggestion-note">
            {strings.email_suggestion_error || strings.suggestions_error || "Could not load suggestions."}
          </p>
        ) : (
          <>
            <div className={`suggestions-chips${pendingOverwrite ? " suggestions-chips-confirming" : ""}`}>
              {items.map((item, index) => (
                <button
                  key={`${item.id}-${item.body.slice(0, 24)}`}
                  type="button"
                  className={`suggestion-chip${selectedIndex === index ? " suggestion-chip-selected" : ""}${pendingOverwrite?.index === index ? " suggestion-chip-pending" : ""}`}
                  title={item.body}
                  disabled={disabled}
                  aria-pressed={pendingOverwrite?.index === index || selectedIndex === index}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClick(item, index);
                  }}
                >
                  {item.label ? (
                    <span className="email-reply-suggestion-label">{labelText(item.label)}</span>
                  ) : null}
                  <span className="suggestion-text">{item.body}</span>
                  <MaterialIcon icon="north_west" size={12} color="#e066ed" className="suggestion-arrow" />
                </button>
              ))}
            </div>
            {pendingOverwrite && (
              <div
                ref={overwritePanelRef}
                className="email-reply-overwrite"
                role="alertdialog"
                aria-labelledby="email-reply-overwrite-title"
                aria-describedby="email-reply-overwrite-preview"
              >
                <div className="email-reply-overwrite-icon">
                  <MaterialIcon icon="swap_horiz" size={18} color="#e066ed" />
                </div>
                <div className="email-reply-overwrite-content">
                  <p id="email-reply-overwrite-title" className="email-reply-overwrite-title">
                    {strings.email_suggestion_overwrite || "Replace the text you already typed with this suggestion?"}
                  </p>
                  <p id="email-reply-overwrite-preview" className="email-reply-overwrite-preview">
                    {pendingOverwrite.item.body}
                  </p>
                  <div className="email-reply-overwrite-actions">
                    <button
                      type="button"
                      className="kj-btn kj-btn--secondary kj-btn--sm"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setPendingOverwrite(null)}
                    >
                      {strings.cancel || "Cancel"}
                    </button>
                    <button
                      type="button"
                      className="kj-btn kj-btn--primary kj-btn--email kj-btn--sm"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applySuggestion(pendingOverwrite.item, pendingOverwrite.index)}
                    >
                      {strings.email_suggestion_replace || "Replace"}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <div className="email-reply-suggestion-actions">
              <span className="email-reply-suggestion-regen-label">
                {strings.email_suggestion_regenerate || "Regenerate"}
              </span>
              <button
                type="button"
                className="email-reply-suggestion-regen"
                disabled={disabled || loading}
                onClick={() => regenerate(undefined, "shorter")}
              >
                {strings.email_suggestion_shorter || "Shorter"}
              </button>
              <button
                type="button"
                className="email-reply-suggestion-regen"
                disabled={disabled || loading}
                onClick={() => regenerate("more_formal")}
              >
                {strings.email_suggestion_more_formal || "More formal"}
              </button>
              <button
                type="button"
                className="email-reply-suggestion-regen"
                disabled={disabled || loading}
                onClick={() => regenerate("friendlier")}
              >
                {strings.email_suggestion_friendlier || "Friendlier"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
